import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import './Carousel.scss';

export interface CarouselProps {
  children: any;
  options?: EmblaOptionsType;
  autoplay?: boolean;
  autoplayOptions?: AutoplayOptionsType;
  indicatorType?: 'dots' | 'line' | 'none';
  maxDots?: number;
  dotColor?: string;
  selectedDotColor?: string;
  onChangeSlide?: (slideNo: number) => void;
  onScroll?: (scrollPosition: number) => void;
  initialScrollPosition?: number;
  styles?: {
    containerStyle?: React.CSSProperties;
    dotsStyle?: React.CSSProperties;
    dotButtonStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    lineStyle?: React.CSSProperties;
    lineTrackStyle?: React.CSSProperties;
    lineProgressStyle?: React.CSSProperties;
  };
}

export const DotButton: React.FC<any> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button type="button" {...restProps}>
      {children}
    </button>
  );
};

export const Carousel: React.FC<CarouselProps> = (props) => {
  const { children, options, autoplay, indicatorType, maxDots, dotColor, selectedDotColor, autoplayOptions } = props;
  const plugins = [];
  if (autoplay) {
    plugins.push(Autoplay(autoplayOptions));
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isInitialized = useRef(false);

  const dotIndices = useMemo(() => {
    const totalSlides = scrollSnaps.length;

    if (!maxDots || totalSlides <= maxDots) {
      return scrollSnaps.map((_, index) => index);
    }

    const halfRange = Math.floor(maxDots / 2);
    const endIndex = Math.min(totalSlides - 1, selectedIndex + halfRange);

    const actualStartIndex = Math.max(0, endIndex - maxDots + 1);
    const actualEndIndex = Math.min(totalSlides - 1, actualStartIndex + maxDots - 1);

    return Array.from({ length: actualEndIndex - actualStartIndex + 1 }, (_, i) => actualStartIndex + i);
  }, [scrollSnaps.length, maxDots, selectedIndex]);

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
    props.onChangeSlide && props.onChangeSlide(emblaApi.selectedScrollSnap());
  }, []);

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    // 스크롤 이벤트를 requestAnimationFrame으로 디바운싱하여 메인쓰레드 블로킹 방지
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        const scrollPosition = emblaApi.internalEngine().location.get();
        props.onScroll && props.onScroll(scrollPosition);
      });
    } else {
      const scrollPosition = emblaApi.internalEngine().location.get();
      props.onScroll && props.onScroll(scrollPosition);
    }
  }, []);

  useEffect(() => {
    if (emblaApi && props.initialScrollPosition !== undefined && !isInitialized.current) {
      isInitialized.current = true;
      emblaApi.internalEngine().scrollTo.distance(props.initialScrollPosition, false);
    }
  }, [emblaApi, props.initialScrollPosition, isInitialized]);

  useEffect(() => {
    if (emblaApi) {
      onInit(emblaApi);
      onSelect(emblaApi);
      emblaApi.on('reInit', onInit);
      emblaApi.on('reInit', onSelect);
      emblaApi.on('select', onSelect);
      emblaApi.on('scroll', onScroll);
    }

    return () => {
      if (emblaApi) {
        emblaApi.off('reInit', onInit);
        emblaApi.off('reInit', onSelect);
        emblaApi.off('select', onSelect);
        emblaApi.off('scroll', onScroll);
        isInitialized.current = false;
      }
    };
  }, [emblaApi, onInit, onSelect, onScroll]);

  return (
    <div className="embla" ref={emblaRef} style={props.styles?.style}>
      <div className="embla__container" style={props.styles?.containerStyle}>
        {children}
      </div>
      {indicatorType === 'dots' && scrollSnaps.length > 1 && (
        <div
          className="embla__dots"
          style={
            {
              ...props.styles?.dotsStyle,
              '--dot-color': dotColor || '#ffffff',
              '--selected-dot-color': selectedDotColor || '#ff3d8f',
            } as React.CSSProperties
          }>
          {dotIndices.map((index) => (
            <DotButton
              key={index}
              onClick={() => scrollTo(index)}
              className={'embla__dot'.concat(index === selectedIndex ? ' embla__dot--selected' : '')}
              style={props.styles?.dotButtonStyle}
            />
          ))}
        </div>
      )}
      {indicatorType === 'line' && scrollSnaps.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 375,
            padding: '0 20px',
            boxSizing: 'border-box',
            ...props.styles?.lineStyle,
          }}>
          <div
            style={{
              width: '100%',
              height: 3,
              borderRadius: 999,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              overflow: 'hidden',
              ...props.styles?.lineTrackStyle,
            }}>
            <div
              style={{
                width: `${((selectedIndex + 1) / scrollSnaps.length) * 100}%`,
                height: '100%',
                borderRadius: 999,
                backgroundColor: '#242424',
                transition: 'width 0.25s ease-out',
                ...props.styles?.lineProgressStyle,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
